import { useContext, useEffect, useState } from "react";
import ContentCreator from "../../../components/ContentCreator/ContentCreator";
import TanStackTable from "../../../components/Table/TanStackTable/TanStackTable";
import { createColumnHelper } from "@tanstack/react-table";
import axios from "axios";
import showNotification from "../../../utils/showNotification";
import AuthContext from "../../../contexts/AuthContext";
import filterFunctions from "../../../utils/filterFunctions";
import SalesLeadsForm from "../SalesLeadsForm/SalesLeadsForm";
import { ERROR_HANDLER } from "../../../utils/error-handler";
import moment from "moment/moment";
import { LEADS_OUTCOME } from "../../../utils/leads-constant";
import { Badge, Button, Flex, Modal, Stack } from "@mantine/core";
import { useNavigate } from "react-router-dom";
import AppContext from "../../../contexts/AppContext";
import { IconChevronRight, IconCopy } from "@tabler/icons-react";
import { handleCopy } from "../../../utils/handleCopy";




const validation = {}

const getLastContactDate = (attempts) => {
    if (!Array.isArray(attempts) || attempts.length === 0) return null;

    const last = attempts.reduce((latest, attempt) =>
        new Date(attempt.date) > new Date(latest.date) ? attempt : latest
    );

    return new Date(last.date);
};

const getInterestAreasFromContacts = (attempts, academicUnits) => {
    if (!Array.isArray(attempts) || attempts.length === 0) return '';

    const labels = attempts
        .filter(item => item?.subject)
        .map(item => {
            const isAcademicUnit = academicUnits.find(aca => item?.subject?.includes(aca.value));
            return isAcademicUnit ? isAcademicUnit.label : item.subject;
        });

    return [...new Set(labels)].join(', ');
};

const getNotesFromContacts = (notes) => {
    if (!Array.isArray(notes) || !notes || notes?.length === 0) return '';
    return notes
        .map(item => {
            return `${item.note}`
        })
        .join(', ');
};


const getSellersByContacts = (attempts) => {
    if (!Array.isArray(attempts) || attempts.length === 0) return 'Sin asignar';

    const lastAttempt = attempts.reduce((latest, current) => {
        const dateLatest = new Date(latest.date);
        const dateCurrent = new Date(current.date);
        return dateCurrent > dateLatest ? current : latest;
    }, attempts[0]);

    const seller = lastAttempt?.seller_id;

    if (!seller || (!seller.name && !seller.lastname)) {
        return "Vendedor no identificado";
    }

    return `${seller.name} ${seller.lastname}`;
};

const getDaysFromLastContact = (attempts) => {
    const lastDate = getLastContactDate(attempts);
    if (!lastDate) return null;

    return moment().diff(moment(lastDate), "days");
};

export const getLastOutcome = (attempts) => {
    if (!Array.isArray(attempts) || attempts.length === 0) return null;

    const last = attempts.reduce((latest, attempt) =>
        new Date(attempt.date) > new Date(latest.date) ? attempt : latest
    );

    return last.outcome || null;
};


const filterBySeller = (row, id, filterValue) => {
    if (!filterValue) return true;

    const sellers = row.getValue(id); // string
    if (!sellers) return false;

    return sellers
        .toLowerCase()
        .includes(filterValue.toLowerCase());
};

const filterRangeDate = (row, id, filterValue) => {
    if (!filterValue || !Array.isArray(filterValue)) return true;

    const [startDate, endDate] = filterValue;
    const attempts = row.original[id];

    if (!Array.isArray(attempts) || attempts.length === 0) return false;

    // Obtener el contacto más reciente
    const lastContact = attempts.reduce((latest, attempt) => {
        const a = new Date(attempt.date);
        const b = new Date(latest.date);
        return a > b ? attempt : latest;
    });

    const date = new Date(lastContact.date);

    return date >= startDate && date <= endDate;
}

const selectFilter = (row, id, filterValue) => {
    if (!filterValue) return true;

    const rowValue = row.getValue(id);
    if (!rowValue) return false;

    const match = LEADS_OUTCOME.find(
        item =>
            item.value.toLowerCase() === filterValue.toLowerCase() ||
            item.label.toLowerCase() === filterValue.toLowerCase()
    );

    const normalized = match?.value;
    if (!normalized) return false;

    return rowValue.toLowerCase() === normalized.toLowerCase();
}

export default function SalesLeadsContainer() {
    const navigate = useNavigate()
    const [tableInstance, setTableInstance] = useState(null)
    const { authToken } = useContext(AuthContext)
    const { academicUnits } = useContext(AppContext)
    const [displayForm, setDisplayForm] = useState(false);
    const [updateData, setUpdateData] = useState(false);
    const [loading, setLoading] = useState(false);
    const [isFetchingLoading, setIsFetchingLoading] = useState(false)
    const [initialValues, setInitialValues] = useState(null)
    const [leadsData, setLeadsData] = useState([])
    const [genericModal, setGenericModal] = useState(null)

    const columnHelper = createColumnHelper();
    const columns = [
        columnHelper.accessor('contactAttempts', {
            cell: (info) => {
                const current_value = info.getValue()
                const value = Array.isArray(current_value) ? current_value : []
                if (value.length === 0) return "Sin contacto"

                const lastContact = value.reduce((latest, attempt) => {
                    const attemptDate = new Date(attempt.date)
                    const latestDate = new Date(latest.date)
                    return attemptDate > latestDate ? attempt : latest
                })

                return moment(lastContact.date).format("DD/MM/YYYY")
            },
            header: "Ultimo contacto",
            size: 180,
            filter: "date",
            enableSorting: false,
            enableColumnFilter: true,
            filterFn: filterRangeDate,
        }),
        columnHelper.accessor(row => getLastOutcome(row.contactAttempts), {
            id: "lastOutcome",
            header: "Estado",
            filter: "select",
            enableSorting: false,
            size: 120,
            filterFn: selectFilter,
            filterOptions: LEADS_OUTCOME,
            cell: info => {
                const outcome = info.getValue();
                if (!outcome) return "Sin contacto";

                const found = LEADS_OUTCOME.find(o => o.value === outcome);
                const text = found?.label || "Sin info";

                const renderColor = (_outcome) => {
                    if (!_outcome || typeof _outcome !== 'string') return 'transparent'
                    const current_outcome = _outcome.toUpperCase()
                    if (current_outcome === "PENDING") return "var(--color-yellow)"
                    if (current_outcome === "SUCCESS") return "var(--color-green)"
                    if (current_outcome === "FAILED") return "var(--color-red)"
                    if (current_outcome === "AUTO-FAILED") return "var(--color-badge-red)"
                    if (current_outcome === "IN-PROGRESS") return "var(--color-badge-yellow)"
                    if (current_outcome === "WAITING-FOR-PAYMENT") return "var(--color-badge-yellow)"
                    return 'transparent'
                }

                const color = renderColor(outcome)

                return <Badge bg={color}>{text}</Badge>;
            }
        }),
        columnHelper.accessor(row => {
            return getDaysFromLastContact(row.contactAttempts);
        }, {
            id: "daysSinceLastContact",
            header: "Días sin contacto",
            cell: (info) => {
                const days = info.getValue();
                return days === null ? "" : `Hace ${days} días`;
            },
            size: 180,
            enableSorting: true,
            enableColumnFilter: false,
        }),
        columnHelper.accessor('name', {
            header: 'Nombre',

            cell: (info) => {
                return info.getValue()

            },
            size: 200,
            filter: "text",
            enableSorting: false,
            filterFn: filterFunctions.textFilter,
        }),
        columnHelper.accessor('lastname', {
            header: 'Apellido',

            cell: (info) => {
                return info.getValue() || ""

            },
            size: 200,
            filter: "text",
            enableSorting: false,
            filterFn: filterFunctions.textFilter,
        }),
        columnHelper.accessor('email', {
            header: () => (
                <Flex w='100%' align='center' justify='center' h='100%' p={0} m={0}>
                    <span style={{ flex: 1, textAlign: "center" }}>
                        Email
                    </span>

                    <Button
                        type="button"
                        variant="transparent"
                        onClick={() => {
                            if (!tableInstance) return;

                            const filteredEmails = tableInstance
                                .getFilteredRowModel()
                                .rows
                                .map(r => r.original.email)
                                .filter(Boolean)
                                .join(", ");

                            handleCopy(filteredEmails, "Se han copiado todos los emails");
                        }}
                    >
                        <IconCopy />
                    </Button>
                </Flex>
            ),

            cell: (info) => {
                const value = info.getValue()
                if (value) return <span onClick={() => {
                    handleCopy(value, "Email copiado correctamente");
                }} style={{ flex: 1, textAlign: "center", cursor: 'pointer' }}>
                    {value}
                </span>
                return "-"
            },
            size: 200,
            filter: "text",
            enableSorting: false,
            filterFn: filterFunctions.textFilter,
        }),
        columnHelper.accessor('phone', {
            cell: (info) => {

                const rawValue = info.getValue();

                const cleanValue = rawValue ? String(rawValue).replace(/\s+/g, '') : null;

                if (cleanValue) return <span onClick={() => {
                    handleCopy(cleanValue, "Teléfono copiado correctamente");
                }} style={{ flex: 1, textAlign: "center", cursor: 'pointer' }}>
                    {cleanValue}
                </span>
                return "-"
            },
            header: () => (
                <Flex w='100%' align='center' justify='center' h='100%' p={0} m={0}>
                    <span style={{ flex: 1, textAlign: "center" }}>
                        Teléfono
                    </span>

                    <Button
                        type="button"
                        variant="transparent"
                        onClick={() => {
                            if (!tableInstance) return;

                            const filteredPhones = tableInstance
                                .getFilteredRowModel()
                                .rows
                                .map(r => r.original.phone)
                                .filter(Boolean)
                                .join(", ");

                            handleCopy(filteredPhones, "Se han copiado todo los teléfonos");
                        }}
                    >
                        <IconCopy />
                    </Button>
                </Flex>
            ),
            size: 200,
            filter: "text",
            enableSorting: false,
            filterFn: (row, id, filterValue) => {
                if (!filterValue) return true;

                const rawValue = row.original[id] ?? "";

                const cleanValue = String(rawValue).replace(/\D/g, '');
                const cleanFilter = String(filterValue).replace(/\D/g, '');

                return cleanValue.includes(cleanFilter);
            },
        }),


        columnHelper.accessor('username', {
            cell: (info) => {
                const value = info.getValue()
                if (value) return (
                    <span
                        onClick={() => {
                            handleCopy(value, "Nombre de usuario (username) copiado correctamente");
                        }}
                        style={{ flex: 1, textAlign: "center", cursor: 'pointer' }}
                    >
                        {value}
                    </span>
                )
                return "-"
            },
            header: () => (
                <Flex w='100%' align='center' justify='center' h='100%' p={0} m={0}>
                    <span style={{ flex: 1, textAlign: "center" }}>
                        Username
                    </span>
                </Flex>
            ),
            size: 200,
            filter: "text",
            enableSorting: false,
            filterFn: filterFunctions.textFilter,
        }),
        columnHelper.accessor(row => {
            return getSellersByContacts(row.contactAttempts);
        }, {
            id: "sellersByContacts",
            header: "Vendedor",
            cell: (info) => {
                return info.getValue();
            },
            size: 180,
            enableSorting: false,
            filterFn: filterBySeller,
        }),

        columnHelper.accessor(row => {
            return getNotesFromContacts(row.notes);
        }, {
            id: "notes",
            header: "Notas",
            cell: (info) => {
                return <span onClick={() => setGenericModal({ data: [...info.getValue().split(',')], title: "Notas" })}>{info.getValue()}</span>;
            },
            size: 100,
            enableSorting: false,
        }),

        columnHelper.accessor(row => {
            return getInterestAreasFromContacts(row.contactAttempts, academicUnits);
        }, {
            id: "interestAreas",
            header: "Áreas de interés",
            cell: (info) => {

                return <span onClick={() => setGenericModal({ data: [...info.getValue().split(',')], title: "Áreas de interés" })}>{info.getValue()}</span>;
            },
            size: 100,
            enableSorting: false,
        }),

    ];


    useEffect(() => {
        const getData = async () => {
            const axiosConfig = {
                headers: {
                    "Content-Type": "application/json",
                    Authorization: `Bearer ${authToken}`
                }
            };
            setIsFetchingLoading(true)
            const URL = `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/leads`
            try {
                const { data } = await axios.get(URL, axiosConfig)
                if (data) {
                    const sortedData = [...data].sort((a, b) => {
                        const getLatest = (lead) => {
                            if (!lead.contactAttempts || lead.contactAttempts.length === 0) return 0;
                            return Math.max(...lead.contactAttempts.map(c => new Date(c.date).getTime()));
                        };

                        const dateA = getLatest(a);
                        const dateB = getLatest(b);

                        return dateB - dateA;
                    });
                    setLeadsData(sortedData)
                }
            } catch (error) {
                showNotification({
                    title: "Ha ocurrido un error",
                    message: error.message || "No se han podido cargar los prospectos"
                })
                console.log(error)
            } finally {
                setIsFetchingLoading(false)
            }
        }
        getData()
    }, [updateData])

    const handleEditLead = (rowData) => {
        if (rowData) {
            setInitialValues({
                ...rowData,
                contactAttempts: rowData?.contactAttempts?.map(item => ({ ...item, seller_id: item?.seller_id?._id || "" }))
            });
            setDisplayForm(true);
        }
    }

    const createLead = async (values, config) => {



        const jsonData = {
            ...values,
            sendEmail: false,
            source: values.leadSource,
        };
        return axios.post(
            `${process.env.REACT_APP_BACKEND_SERVER}info/createLead`,
            jsonData,
            config
        );
    };

    const editLead = async (values, config, _uploadToS3) => {
        const jsonData = {
            ...values,
            contactAttempts: values.contactAttempts?.map(item => ({ ...item, seller_id: item?.seller_id || null }))
        };

        return axios.put(
            `${process.env.REACT_APP_BACKEND_SERVER}admin/ventas/leads/${values._id}`,
            jsonData,
            config
        );
    };


    const handleSubmit = async (values) => {
        const config = {
            headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${authToken}`,
            },
        };

        try {
            setLoading(true);
            let notificationMessage = ''

            if (initialValues) {
                await editLead(values, config);
                notificationMessage = `El prospecto ha sido editado con éxito.`
            } else {
                await createLead(values, config);
                notificationMessage = `El prospecto ha sido creado con éxito.`
            }

            showNotification({
                color: "green",
                status: "success",
                title: "¡Excelente! Operación exitosa.",
                message: notificationMessage,
            });



            if (values.createSale) {
                navigate('/admin/ventas/pagos-ingresados', {
                    state: {
                        name: values.name?.trim(),
                        lastname: values.lastname?.trim(),
                        email: values.email?.trim(),
                        phone: values.phone?.trim(),
                        dni: "",
                        currency: "USD",
                        paymentMethod: "transfer",
                        academicUnitId: values.createSale,
                        source: "ACADEMIC_UNIT",
                        sellerId: values.current_seller_id,
                        alreadyUser: false,
                        amountDeposited: {
                            amount: 0,
                            currency: "USD"
                        }
                    }
                })
            }

            setUpdateData(!updateData);
            setDisplayForm(false);
            setInitialValues(null);

        } catch (error) {
            showNotification({
                color: "red",
                status: "error",
                title: "Operación fallida.",
                message: `No se pudo crear / editar la inscripción. Detalle: ${ERROR_HANDLER[error.request?.status] || ERROR_HANDLER.defaultError
                    }`,
                autoClose: 12000,
            });
        } finally {
            setLoading(false);
        }
    };

    return (
        <div className="salesBulkMessages__container">
            {displayForm && (
                <ContentCreator
                    title={initialValues ? "Editar prospecto" : "Crear prospecto"}
                    submitFunc={(values) => handleSubmit(values)}
                    onCancel={() => {
                        setDisplayForm(false);
                        setInitialValues(null);
                    }}
                    validation={validation}
                    initialValues={initialValues ?? {
                        contactAttempts: [],
                        notes: []
                    }}
                    renderPreview={(_form) => (
                        <></>
                    )}
                    renderForm={(_form) => (
                        <SalesLeadsForm form={_form.form} loading={loading} />
                    )}
                />
            )}
            <TanStackTable
                onLoadTable={(table) => setTableInstance(table)}
                data={leadsData}
                columns={columns}
                editFunc={handleEditLead}
                loading={loading || isFetchingLoading}
                displayForm={displayForm}
                addButtonFunc={() => {
                    setDisplayForm(!displayForm);
                    if (displayForm) setInitialValues(null);
                }}
            />
            {genericModal && <Modal
                opened={genericModal}
                onClose={() => setGenericModal(null)}>
                {genericModal?.title && <h1 style={{ fontSize: "24px" }}>{genericModal?.title}</h1>}
                <Stack gap={4}>
                    {genericModal?.data?.map(item =>
                        <Flex key={item}>
                            <IconChevronRight />
                            <p style={{ fontSize: "16px" }} >{item}</p>
                        </Flex>
                    )}
                </Stack>
                <Button mt={16} type='button' onClick={() => setGenericModal(null)}>
                    Cerrar
                </Button>
            </Modal>}
        </div>
    );
}